<template>
  <el-input
    ref="phoneInput"
    :model-value="displayValue"
    class="account-person-form__phone-field"
    :class="{ 'account-person-form__phone-field--partial': digits.length < 9 }"
    inputmode="numeric"
    @keydown="handleKeydown"
    @paste="handlePaste"
    @input="handleInput"
  >
    <template #prefix>
      <span class="account-person-form__phone-inline-code">+994</span>
    </template>
  </el-input>
</template>

<script>
import {
  formatAzMobileMaskTemplate,
  getAzMobileCursorPos,
  getAzMobileDigitIndexFromCursor,
  parseAzMobileDigits,
} from "@/project/Source/helper.js";

export default {
  name: "AzPhoneInput",
  props: {
    modelValue: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue"],
  computed: {
    digits() {
      return parseAzMobileDigits(this.modelValue);
    },
    displayValue() {
      return formatAzMobileMaskTemplate(this.modelValue);
    },
  },
  methods: {
    getNativeInput() {
      return this.$refs.phoneInput?.ref ?? this.$refs.phoneInput?.input;
    },
    updateDigits(newDigits, cursorDigitIndex) {
      this.$emit("update:modelValue", newDigits);
      this.$nextTick(() => this.setCursor(cursorDigitIndex));
    },
    setCursor(digitIndex) {
      const el = this.getNativeInput();
      if (!el) return;
      const pos = getAzMobileCursorPos(digitIndex);
      el.setSelectionRange(pos, pos);
    },
    restoreDisplay(cursorDigitIndex) {
      this.$nextTick(() => {
        const el = this.getNativeInput();
        if (!el) return;
        const idx =
          cursorDigitIndex ?? getAzMobileDigitIndexFromCursor(el.selectionStart ?? 0);
        if (el.value !== this.displayValue) {
          el.value = this.displayValue;
        }
        this.setCursor(idx);
      });
    },
    handleInput(event) {
      const parsed = parseAzMobileDigits(event.target.value).slice(0, 9);
      if (parsed !== this.digits) {
        this.updateDigits(parsed, parsed.length);
        return;
      }
      this.restoreDisplay();
    },
    handlePaste(event) {
      event.preventDefault();
      const text = event.clipboardData?.getData("text") || "";
      const pasted = parseAzMobileDigits(text);
      if (!pasted) return;

      const input = event.target;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? start;
      const startIdx = getAzMobileDigitIndexFromCursor(start);
      const endIdx = getAzMobileDigitIndexFromCursor(end);
      const next = (this.digits.slice(0, startIdx) + pasted + this.digits.slice(endIdx)).slice(0, 9);
      this.updateDigits(next, Math.min(startIdx + pasted.length, next.length));
    },
    handleKeydown(event) {
      const input = event.target;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? start;

      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();

        if (end > start) {
          const startIdx = getAzMobileDigitIndexFromCursor(start);
          const endIdx = getAzMobileDigitIndexFromCursor(end);
          const next = this.digits.slice(0, startIdx) + this.digits.slice(endIdx);
          this.updateDigits(next, startIdx);
          return;
        }

        if (event.key === "Backspace") {
          const idx = getAzMobileDigitIndexFromCursor(start);
          if (idx <= 0) return;
          const next = this.digits.slice(0, idx - 1) + this.digits.slice(idx);
          this.updateDigits(next, idx - 1);
          return;
        }

        const idx = getAzMobileDigitIndexFromCursor(start);
        if (idx >= this.digits.length) return;
        const next = this.digits.slice(0, idx) + this.digits.slice(idx + 1);
        this.updateDigits(next, idx);
        return;
      }

      if (/^\d$/.test(event.key)) {
        event.preventDefault();

        if (end > start) {
          const startIdx = getAzMobileDigitIndexFromCursor(start);
          const endIdx = getAzMobileDigitIndexFromCursor(end);
          const next = (this.digits.slice(0, startIdx) + event.key + this.digits.slice(endIdx)).slice(0, 9);
          this.updateDigits(next, Math.min(startIdx + 1, next.length));
          return;
        }

        const idx = getAzMobileDigitIndexFromCursor(start);
        if (idx >= 9) return;
        const next = (this.digits.slice(0, idx) + event.key + this.digits.slice(idx)).slice(0, 9);
        this.updateDigits(next, idx + 1);
        return;
      }

      const allowed = ["Tab", "ArrowLeft", "ArrowRight", "Home", "End"];
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (!allowed.includes(event.key)) {
        event.preventDefault();
      }
    },
  },
};
</script>
